/**
 * Tenant-facing developer portal.
 *
 * Sidebar-grouped guides rendered from markdown drafts in ./guides/.
 * URL query param ?guide=<slug> drives the active page so each guide
 * is shareable. Content is intentionally tenant-agnostic: every
 * example uses placeholder ids (cst_*, hld_*, prd_*, clm_*, wty_*,
 * evt_*, sub_*) so the same prose lifts straight to a public
 * docs.iwarranty.ai/ site later without scrubbing.
 *
 * {{API_BASE}} in markdown is replaced at render time with the
 * actual API base so curl examples are copy-pasteable.
 */

import { useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { BookOpen, Download, ExternalLink } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { AppShell } from '@/admin/components/app-shell'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import {
  buildPublicApiUrl,
  resolveDocsApiBase,
} from '@/admin/lib/public-api-url'

import gettingStarted from './guides/01-getting-started.md?raw'
import authentication from './guides/02-authentication.md?raw'
import resourceModel from './guides/03-resource-model.md?raw'
import registerHolding from './guides/04-flow-register-a-holding.md?raw'
import raiseClaim from './guides/05-flow-raise-a-claim.md?raw'
import workflowActions from './guides/06-workflow-actions.md?raw'
import paginationErrors from './guides/07-pagination-and-errors.md?raw'
import pulseOverview from './guides/10-pulse-overview.md?raw'
import pulseSubscribing from './guides/11-pulse-subscribing.md?raw'
import pulseEnvelope from './guides/12-pulse-envelope.md?raw'
import pulseSigning from './guides/13-pulse-signing.md?raw'
import pulseRetries from './guides/14-pulse-retries.md?raw'
import pulseEvents from './guides/15-pulse-events.md?raw'
import powerBiReports from './guides/08-powerbi-reports.md?raw'

interface Guide {
  slug: string
  label: string
  source: string
}

interface Section {
  label: string
  guides: Guide[]
}

const SECTIONS: Section[] = [
  {
    label: 'Getting started',
    guides: [
      { slug: '01-getting-started', label: 'Welcome', source: gettingStarted },
      { slug: '02-authentication', label: 'Authentication', source: authentication },
      { slug: '07-pagination-and-errors', label: 'Pagination & errors', source: paginationErrors },
    ],
  },
  {
    label: 'Resources',
    guides: [
      { slug: '03-resource-model', label: 'Resource model', source: resourceModel },
    ],
  },
  {
    label: 'Recipes',
    guides: [
      { slug: '04-flow-register-a-holding', label: 'Register a holding', source: registerHolding },
      { slug: '05-flow-raise-a-claim', label: 'Raise a claim', source: raiseClaim },
      { slug: '06-workflow-actions', label: 'Workflow actions', source: workflowActions },
    ],
  },
  {
    label: 'Reporting',
    guides: [
      { slug: '08-powerbi-reports', label: 'Power BI & report datasets', source: powerBiReports },
    ],
  },
  {
    label: 'Pulse webhooks',
    guides: [
      { slug: '10-pulse-overview', label: 'Overview', source: pulseOverview },
      { slug: '11-pulse-subscribing', label: 'Subscribing to events', source: pulseSubscribing },
      { slug: '12-pulse-envelope', label: 'Event envelope', source: pulseEnvelope },
      { slug: '13-pulse-signing', label: 'Signing & rotation', source: pulseSigning },
      { slug: '14-pulse-retries', label: 'Retries & replay', source: pulseRetries },
      { slug: '15-pulse-events', label: 'Event catalog', source: pulseEvents },
    ],
  },
]

const ALL_GUIDES: Guide[] = SECTIONS.flatMap((s) => s.guides)
const DEFAULT_SLUG = ALL_GUIDES[0].slug

export function DevelopersPage() {
  const [params, setParams] = useSearchParams()
  const activeSlug = params.get('guide') ?? DEFAULT_SLUG
  const active = useMemo(
    () => ALL_GUIDES.find((g) => g.slug === activeSlug) ?? ALL_GUIDES[0],
    [activeSlug],
  )
  const apiBase = useMemo(
    () => resolveDocsApiBase(window.location.hostname),
    [],
  )
  const schemaUrl = useMemo(
    () => buildPublicApiUrl('/api/v1/schema/', window.location.hostname),
    [],
  )
  const referenceUrl = useMemo(
    () => buildPublicApiUrl('/api/v1/reference/', window.location.hostname),
    [],
  )
  const rendered = useMemo(
    () => active.source.replaceAll('{{API_BASE}}', apiBase),
    [active, apiBase],
  )

  function goTo(slug: string) {
    setParams({ guide: slug })
  }

  return (
    <AppShell>
      <div className="flex h-[calc(100vh-64px)] min-h-0 flex-col">
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b-[0.5px] border-[var(--rule)] px-6 py-4">
          <div className="flex items-center gap-3">
            <BookOpen size={18} className="text-[var(--ink-3)]" />
            <div>
              <h1 className="text-[16px] font-medium text-[var(--ink)]">
                Developer portal
              </h1>
              <p className="text-[12px] text-[var(--ink-3)]">
                Guides, recipes, and live API reference for the iWarranty
                Public API.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                window.open(schemaUrl, '_blank', 'noopener,noreferrer')
              }
            >
              <Download size={14} />
              Download OpenAPI
            </Button>
            <Button
              size="sm"
              onClick={() =>
                window.open(referenceUrl, '_blank', 'noopener,noreferrer')
              }
            >
              <ExternalLink size={14} />
              API reference
            </Button>
          </div>
        </div>

        {/* Sidebar + content */}
        <div className="flex min-h-0 flex-1">
          <aside className="w-64 shrink-0 overflow-y-auto border-r-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-6">
            {SECTIONS.map((section) => (
              <div key={section.label} className="mb-6">
                <p className="mb-2 px-2 text-[10px] font-medium uppercase tracking-[0.16em] text-[var(--ink-3)]">
                  {section.label}
                </p>
                <ul className="flex flex-col gap-0.5">
                  {section.guides.map((g) => {
                    const isActive = g.slug === active.slug
                    return (
                      <li key={g.slug}>
                        <button
                          type="button"
                          onClick={() => goTo(g.slug)}
                          className={cn(
                            'w-full rounded-[4px] px-2 py-1.5 text-left text-[12.5px] transition-colors',
                            isActive
                              ? 'bg-[var(--green-bg-subtle)] text-[var(--green)]'
                              : 'text-[var(--ink-2)] hover:bg-[var(--bg-card)] hover:text-[var(--ink)]',
                          )}
                        >
                          {g.label}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </aside>

          <article className="min-w-0 flex-1 overflow-y-auto px-10 py-8">
            <div className="mx-auto max-w-[720px]">
              <DocsMarkdown source={rendered} onNavigate={goTo} />
            </div>
          </article>
        </div>
      </div>
    </AppShell>
  )
}

// ---------------------------------------------------------------------------
// markdown rendering
// ---------------------------------------------------------------------------

interface DocsMarkdownProps {
  source: string
  onNavigate: (slug: string) => void
}

function DocsMarkdown({ source, onNavigate }: DocsMarkdownProps) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => (
          <h1 className="mb-6 font-display text-[32px] leading-tight text-[var(--ink)]">
            {children}
          </h1>
        ),
        h2: ({ children }) => (
          <h2 className="mb-3 mt-10 text-[18px] font-medium text-[var(--ink)]">
            {children}
          </h2>
        ),
        h3: ({ children }) => (
          <h3 className="mb-2 mt-6 text-[14px] font-medium text-[var(--ink-2)]">
            {children}
          </h3>
        ),
        p: ({ children }) => (
          <p className="mb-4 text-[13.5px] leading-[1.65] text-[var(--ink-2)]">
            {children}
          </p>
        ),
        ul: ({ children }) => (
          <ul className="mb-4 ml-5 list-disc space-y-1.5 text-[13.5px] leading-[1.6] text-[var(--ink-2)]">
            {children}
          </ul>
        ),
        ol: ({ children }) => (
          <ol className="mb-4 ml-5 list-decimal space-y-1.5 text-[13.5px] leading-[1.6] text-[var(--ink-2)]">
            {children}
          </ol>
        ),
        li: ({ children }) => <li className="pl-1">{children}</li>,
        strong: ({ children }) => (
          <strong className="font-semibold text-[var(--ink)]">{children}</strong>
        ),
        code: ({ children, className }) => {
          if (!className) {
            return (
              <code className="rounded-[3px] bg-[var(--bg-subtle)] px-1.5 py-0.5 font-mono text-[12px] text-[var(--ink)]">
                {children}
              </code>
            )
          }
          return <code className={className}>{children}</code>
        },
        pre: ({ children }) => (
          <pre className="mb-5 overflow-x-auto rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4 font-mono text-[12px] leading-[1.55] text-[var(--ink)]">
            {children}
          </pre>
        ),
        a: ({ href, children }) => {
          if (href?.startsWith('?guide=')) {
            const slug = href.replace('?guide=', '')
            return (
              <button
                type="button"
                onClick={() => onNavigate(slug)}
                className="cursor-pointer text-[var(--green)] underline decoration-[var(--green)]/40 hover:decoration-[var(--green)]"
              >
                {children}
              </button>
            )
          }
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--green)] underline decoration-[var(--green)]/40 hover:decoration-[var(--green)]"
            >
              {children}
            </a>
          )
        },
        table: ({ children }) => (
          <div className="mb-5 overflow-x-auto rounded-[6px] border-[0.5px] border-[var(--rule)]">
            <table className="w-full border-collapse text-[12.5px]">
              {children}
            </table>
          </div>
        ),
        th: ({ children }) => (
          <th className="border-b-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2 text-left text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="border-t-[0.5px] border-t-[var(--rule-light)] px-3 py-2 align-top text-[var(--ink-2)]">
            {children}
          </td>
        ),
        blockquote: ({ children }) => (
          <blockquote className="mb-4 border-l-[2px] border-[var(--gold-border)] bg-[var(--gold-bg)] px-4 py-2 text-[13px] text-[var(--gold-ink)]">
            {children}
          </blockquote>
        ),
        hr: () => <hr className="my-8 border-t-[0.5px] border-[var(--rule)]" />,
      }}
    >
      {source}
    </ReactMarkdown>
  )
}
